import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageSplit from 'assets/components/page/themes/Theme.Page.Split';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import { replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty, isNull } from 'lodash';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import VertaforeStatuses from 'models/enums/VertaforeDocumentStatuses';
import contractSetStatusUnrestrictedApi from 'models/sales/contract/setStatusUnrestricted/Api.Contract.SetStatusUnrestricted';
import { isMobile } from 'react-device-detect';
import { salesPayAndSignContractIndexRoute } from '../../payAndSignContract/Sales.PayAndSignContract.Index';
import { SalesContractAction, SalesContractInfoTab, useSalesContractProvider } from '../Sales.Contract.Hooks';
import ContractInfoDocuments from './info/Contract.Info.Documents';
import ContractInfoGeneralInfo from './info/Contract.Info.GeneralInfo';
import ContractInfoNavigation from './info/Contract.Info.Navigation';
import ContractInfoNotes from './info/Contract.Info.Notes';
import ContractInfoRevisions from './info/Contract.Info.Revisions';
import ContractInfoStatusAuditLogs from './info/Contract.Info.StatusAuditLogs';

export default function ContractInfo() {
  const { lang } = getLocales();
  const { isDealer } = useAuthStore();
  const {
    selected,
    pageRouter,
    permissions,
    statuses,
    syncContractWithVertafore,
    syncContractDocumentWithVertafore,
    regenerateContractDocuments,
    listApi,
    contractStatusAuditLogListApi,
    isStatusChanging,
    setIsStatusChanging,
  } = useSalesContractProvider();

  const isCustomerConsentRequired = selected?.product?.isCustomerConsentRequired
    ? selected?.contractConsent?.isAccepted === true
    : true;
  const requiresPayment = !selected?.isPayed && selected?.dealer?.isPaymentRequired && isCustomerConsentRequired;
  const requiresSignature =
    !selected?.isSignedManually &&
    !selected?.isSignedWithDocuSing &&
    !selected?.isSentForSigning &&
    isCustomerConsentRequired;
  const canRegenerateContractDocuments = isEmpty(selected?.documents);
  const canResendConsent = isNull(selected?.contractConsent?.isAccepted);

  //#region Vertafore bool vars
  const isContractSyncable =
    selected?.product?.isStoredInBMS &&
    selected?.statusCode !== ContractStatusCode.CANCELED_VOID &&
    selected?.statusCode !== ContractStatusCode.CANCELED_REFUND_PR_REFUND;

  const isContractDocumentSyncedWithVertafore =
    isContractSyncable &&
    selected?.client?.vertaforeCustomerNumber &&
    (selected?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ||
      selected?.vertaforeStatus === VertaforeStatuses.ERROR_UPLOAD_DOCUMENT);

  const isContractSyncedWithVertafore =
    isContractSyncable && !selected?.client?.vertaforeCustomerNumber
      ? selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_CLIENT ||
        selected?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ||
        selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY
      : selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY;
  //#endregion

  return !selected ? null : (
    <Page
      class={ThemePageSplit}
      isLoading={isStatusChanging}
      header={
        <Card
          class={ThemeCardRow}
          media={<Icon class="if-database" />}
          info={selected?.statusCode?.replace('_', ' - ')}
          title={selected?.appNumberWithRevision}
          subtitle={selected?.vehicle?.vin}
          description={
            <>
              {selected?.isPayed && (
                <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.success }}>
                  <Icon class="fas-check" />
                  &nbsp; Paid
                </Box>
              )}
              {(selected?.isSignedManually || selected?.isSignedWithDocuSing || selected?.isSentForSigning) && (
                <Box
                  class={ThemeTagRounded}
                  style={{
                    backgroundColor:
                      selected?.isSignedManually || selected?.isSignedWithDocuSing
                        ? Theme.Color.success
                        : Theme.Color.warning,
                  }}
                >
                  <Icon class="fas-check" />
                  &nbsp;
                  {selected?.isSignedManually
                    ? lang.signedManually
                    : selected?.isSignedWithDocuSing
                    ? lang.signedWithDocuSign
                    : lang.sentForSigning}
                </Box>
              )}
              {!selected?.client?.vertaforeCustomerNumber ? (
                selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_CLIENT ? (
                  <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                    <Icon class="fas-ban" />
                    &nbsp;
                    {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, { status: 'FAILED' })}
                  </Box>
                ) : (
                  selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY && (
                    <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                      <Icon class="fas-ban" />
                      &nbsp;
                      {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, { status: 'CREATING POLICY' })}
                    </Box>
                  )
                )
              ) : selected?.vertaforeStatus === VertaforeStatuses.ERROR_UPLOAD_DOCUMENT ? (
                <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                  <Icon class="fas-ban" />
                  &nbsp;
                  {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                    status: 'DOCUMENT UPLOAD FAILED',
                  })}
                </Box>
              ) : selected?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ? (
                <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                  <Icon class="fas-ban" />
                  &nbsp;
                  {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, { status: 'DOCUMENT NOT UPLOADED' })}
                </Box>
              ) : selected?.vertaforeStatus === VertaforeStatuses.UNSIGNED_UPLOADED ||
                selected?.vertaforeStatus === VertaforeStatuses.SIGNED_UPLOADED ? (
                <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.success }}>
                  <Icon class="fas-check" />
                  &nbsp; {lang.syncedWithVertafore}
                </Box>
              ) : selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY ? (
                <Box class={ThemeTagRounded} style={{ backgroundColor: Theme.Color.error }}>
                  <Icon class="fas-ban" />
                  &nbsp;
                  {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, { status: 'CREATING POLICY' })}
                </Box>
              ) : null}
            </>
          }
        />
      }
      sidebar={
        <>
          {(!pageRouter.query.infoTab || pageRouter.query.infoTab === SalesContractInfoTab.INFO) && (
            <ContractInfoGeneralInfo />
          )}
          {pageRouter.query.infoTab === SalesContractInfoTab.DOCUMENTS && <ContractInfoDocuments />}
          {pageRouter.query.infoTab === SalesContractInfoTab.REVISIONS && <ContractInfoRevisions />}
          {pageRouter.query.infoTab === SalesContractInfoTab.NOTES && <ContractInfoNotes />}
          {pageRouter.query.infoTab === SalesContractInfoTab.STATUS_AUDIT_LOGS && <ContractInfoStatusAuditLogs />}
        </>
      }
      render={<ContractInfoNavigation />}
      footer={
        <>
          {permissions?.EDIT_CONTRACT_UNRESTRICTED && selected?.statusCode === 'OPEN_SUBMITTED' && !isDealer && (
            <Button
              render={lang.editContractUnrestricted}
              class={ThemeButtonCircle}
              onClick={pageRouter.updateParams}
              media={<Icon class="fas-coins" />}
              data={{ id: selected.id.toString(), action: SalesContractAction.ADJUST_DISTRIBUTIONS }}
            />
          )}
          {permissions?.UPDATE_CONTRACT_STATUS_UNRESTRICTED && (
            <DropdownButton
              buttonMedia={<Icon class="fas-check-double" />}
              horizontalPosition="auto_fixed"
              verticalPosition="auto_fixed"
              subcomponents={ThemeDropdownButtonPopup}
              buttonBody={lang.changeStatus}
              htmlElementProps={{ title: lang.changeStatus }}
              disabled={!permissions?.UPDATE_CONTRACT_STATUS_UNRESTRICTED || !statuses?.length}
              tabIndex={null}
              dropdownOptions={({ animateOut }) => (
                <DataMap
                  data={statuses}
                  render={({ data: status }) => (
                    <Button
                      key={status.id}
                      onClick={async (newStatus) => {
                        animateOut();

                        if (newStatus.id !== ContractStatusCode.CANCELED_REFUND_PR_REFUND) {
                          setIsStatusChanging(true);
                          const res = await contractSetStatusUnrestrictedApi(selected?.id, {
                            newStatus: newStatus.id as Model.Enum.ContractStatusCode,
                          });
                          if (res.status === HttpStatus.ok) {
                            setIsStatusChanging(false);
                            addLog({ success: lang.contractStatusSuccesfullyChanged });
                            listApi.execute((b) => b);
                            contractStatusAuditLogListApi.execute((b) => b);
                          } else {
                            setIsStatusChanging(false);
                            addLog({ error: res.message });
                          }
                        } else {
                          pageRouter.updateParams({
                            id: selected?.id?.toString(),
                            action: 'cancellation-unrestricted',
                          });
                        }
                      }}
                      data={status}
                      render={status.id}
                      class={ThemeButtonMenuItem}
                    />
                  )}
                />
              )}
            />
          )}
          {(requiresPayment || requiresSignature) && (
            <LinkButton
              class={ThemeButtonCircle}
              url={createPathFromRoute(
                salesPayAndSignContractIndexRoute,
                {},
                {
                  contractId: selected.id,
                }
              )}
              media={<Icon class="fas-receipt" />}
              render={requiresPayment && requiresSignature ? 'Pay & sign' : requiresPayment ? 'Pay' : 'Sign'}
            />
          )}
          {canResendConsent && (
            <Button
              media={<Icon class="fas-envelope" />}
              onClick={pageRouter.updateParams}
              data={{
                id: selected?.id?.toString(),
                action: 'resendConsent',
              }}
              render={lang.resendConsent}
              class={ThemeButtonCircle}
            />
          )}
          {isContractSyncedWithVertafore && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class="fas-sync" />}
              render={!isMobile && lang.syncContractWithVertafore}
              onClick={syncContractWithVertafore}
              data={{ contractId: selected?.id }}
              disabled={!permissions.VERTAFORE_SYNC_CONTRACT}
            />
          )}
          {isContractDocumentSyncedWithVertafore && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class="fas-sync" />}
              render={!isMobile && lang.syncContractDocumentationAggregateWithVertafore}
              onClick={syncContractDocumentWithVertafore}
              data={{ contractId: selected?.id }}
              disabled={!permissions.VERTAFORE_SYNC_CONTRACT}
            />
          )}
          {canRegenerateContractDocuments && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class="if-refresh" />}
              render={!isMobile && lang.regenerateContractDocuments}
              onClick={regenerateContractDocuments}
              data={selected}
              disabled={!permissions.EDIT_CONTRACT}
            />
          )}
        </>
      }
    />
  );
}
